import React, { useState, useEffect, useCallback, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { LanguageProvider, useI18n } from "./i18n";

// Google reCAPTCHA v2 site key — replace with your real key from https://www.google.com/recaptcha/admin
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const BRAND = { pink: "#ff0054", pinkLight: "#ff0076", orange: "#ff4d2a", lime: "#ccff00", gray: "#979797" };
const grad = `linear-gradient(135deg, #ff0076, #ff0054 40%, #ff4d2a)`;

const DEFAULT_SETTINGS = {
  sweepValue: 40, sweepUnit: "minutes", schedulerEnabled: false,
  alertEmails: [], alertScoreThreshold: 65, reportHour: 12, reportMinute: 0,
  reportTimezone: "Asia/Jerusalem", reportEmails: [],
  outputLanguage: "Hebrew",
  autoSweepSinceMode: "last_sweep", autoSweepMaxPosts: 50,
};

const DEFAULT_STATS = {
  postsScanned: 0, qualifiedLeads: 0, pendingSend: 0, sentToday: 0,
  aiCostUsd: 0, avgScore: 0, scoreHigh: 0, scoreMid: 0, scoreLow: 0,
  relevantTrue: 0, relevantFalse: 0, relevantUntagged: 0,
  recentLeads: [], scoreChart: [], unassignedLeads: 0, leadsPerUser: [],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem("lr_jwt");
const setToken = (t) => { if (t) localStorage.setItem("lr_jwt", t); else localStorage.removeItem("lr_jwt"); };

const apiFetch = async (path, opts = {}) => {
  const token = getToken();
  const res = await fetch(`${API}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    ...opts,
  });
  if (res.status === 401) { setToken(null); window.dispatchEvent(new CustomEvent("lr:unauthorized")); return; }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }) +
    " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

// ─── UI Primitives ────────────────────────────────────────────────────────────
function RadarIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2" /><circle cx="12" cy="12" r="6" strokeOpacity=".5" /><circle cx="12" cy="12" r="10" strokeOpacity=".25" />
      <line x1="12" y1="12" x2="19" y2="5" strokeWidth="2.5" /><polyline points="17,5 19,5 19,7" strokeWidth="2" />
    </svg>
  );
}

const Btn = ({ children, onClick, style = {}, variant = "primary", small, disabled }) => {
  const base = { fontFamily: "Sen, sans-serif", fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", border: "none", borderRadius: 8, padding: small ? "6px 14px" : "10px 22px", fontSize: small ? 12 : 14, transition: "opacity .15s", display: "inline-flex", alignItems: "center", gap: 6, opacity: disabled ? .5 : 1 };
  const variants = { primary: { background: grad, color: "#fff" }, outline: { background: "transparent", color: BRAND.pink, border: `2px solid ${BRAND.pink}` }, ghost: { background: "#f3f4f6", color: "#374151" }, danger: { background: "#fee2e2", color: "#dc2626" } };
  return <button style={{ ...base, ...variants[variant], ...style }} onClick={disabled ? undefined : onClick}>{children}</button>;
};

const Toggle = ({ active, onToggle }) => (
  <div onClick={e => { e.stopPropagation(); onToggle(); }} style={{ cursor: "pointer", width: 40, height: 22, borderRadius: 11, background: active ? BRAND.pink : "#e5e7eb", transition: "background .2s", position: "relative", flexShrink: 0 }}>
    <div style={{ position: "absolute", top: 3, left: active ? 21 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 4px rgba(0,0,0,.2)" }} />
  </div>
);

const Badge = ({ score, large }) => {
  const color = score >= 80 ? "#16a34a" : score >= 65 ? "#ca8a04" : BRAND.gray;
  const bg = score >= 80 ? "#dcfce7" : score >= 65 ? "#fef9c3" : "#f3f4f6";
  return <span style={{ background: bg, color, fontWeight: 800, fontSize: large ? 18 : 13, padding: large ? "6px 16px" : "3px 10px", borderRadius: 20 }}>{score}</span>;
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 1px 8px rgba(0,0,0,.07)", border: "1px solid #f0f0f0", ...style }}>{children}</div>
);

const SectionTitle = ({ children, sub }) => {
  const { dir } = useI18n();
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: "#1a1a1a", borderLeft: dir === "rtl" ? "none" : `4px solid ${BRAND.pink}`, borderRight: dir === "rtl" ? `4px solid ${BRAND.pink}` : "none", paddingLeft: dir === "rtl" ? 0 : 10, paddingRight: dir === "rtl" ? 10 : 0 }}>{children}</div>
      {sub && <div style={{ fontSize: 12, color: BRAND.gray, marginTop: 4, paddingLeft: dir === "rtl" ? 0 : 14, paddingRight: dir === "rtl" ? 14 : 0 }}>{sub}</div>}
    </div>
  );
};

const Input = ({ value, onChange, placeholder, style = {}, type = "text", onKeyDown, dir }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown} dir={dir}
    style={{ fontFamily: "Sen, sans-serif", fontSize: 13, border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", outline: "none", width: "100%", boxSizing: "border-box", ...style }} />
);

const Textarea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
    style={{ fontFamily: "Sen, sans-serif", fontSize: 13, border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", outline: "none", width: "100%", resize: "vertical", boxSizing: "border-box" }} />
);

const Pill = ({ label, active, onClick, color }) => (
  <button onClick={onClick} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "6px 16px", borderRadius: 20, border: `1.5px solid ${active ? (color || BRAND.pink) : "#e5e7eb"}`, background: active ? (color ? color + "18" : "#fff0f5") : "#f8f9fc", color: active ? (color || BRAND.pink) : "#666", transition: "all .15s", whiteSpace: "nowrap" }}>{label}</button>
);

const TimeRangeSelector = ({ value, onChange }) => {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 10, padding: 3, gap: 2 }}>
      {[["24h", t("time.24h")], ["7d", t("time.7d")], ["30d", t("time.30d")], ["all", t("time.all")]].map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "6px 14px", borderRadius: 8, border: "none", background: value === v ? "#fff" : "transparent", color: value === v ? BRAND.pink : "#777", boxShadow: value === v ? "0 1px 6px rgba(0,0,0,.1)" : "none", transition: "all .15s", whiteSpace: "nowrap" }}>{l}</button>
      ))}
    </div>
  );
};

const FbBtn = ({ url }) => {
  const { t } = useI18n();
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 12, color: "#1877f2", background: "#e7f0fd", border: "1.5px solid #b3cef5", padding: "5px 12px", borderRadius: 8, textDecoration: "none", whiteSpace: "nowrap" }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="#1877f2"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" /></svg>
      {t("goToPost")}
    </a>
  );
};

const RelevanceBtns = ({ lead, onSet }) => {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", gap: 5 }} onClick={e => e.stopPropagation()}>
      <button onClick={() => onSet(lead.relevant === true ? null : true)} title={t("relevant.title")} style={{ background: lead.relevant === true ? "#dcfce7" : "#f3f4f6", border: `1.5px solid ${lead.relevant === true ? "#16a34a" : "#e5e7eb"}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>👍</button>
      <button onClick={() => onSet(lead.relevant === false ? null : false)} title={t("notRelevant.title")} style={{ background: lead.relevant === false ? "#fee2e2" : "#f3f4f6", border: `1.5px solid ${lead.relevant === false ? "#dc2626" : "#e5e7eb"}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>👎</button>
    </div>
  );
};

const Spinner = () => {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40, color: BRAND.gray, fontSize: 13 }}>
      <span style={{ display: "inline-block", animation: "spin 1s linear infinite", marginRight: 8 }}>⟳</span> {t("loading")}
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div style={{ padding: 40, textAlign: "center", color: BRAND.gray, fontSize: 13 }}>{message}</div>
);

const ApiNotice = () => {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 12, color: "#92400e" }}>
      <span>⚡</span> <span>{t('apiNotice')}</span>
    </div>
  );
};

// ─── Sweep Modal ──────────────────────────────────────────────────────────────
const getSweepModes = (t) => [
  { id: "last_sweep",  label: t("sweep.last_sweep.label"),  sub: t("sweep.last_sweep.sub") },
  { id: "last_day",    label: t("sweep.last_day.label"),    sub: t("sweep.last_day.sub") },
  { id: "last_3_days", label: t("sweep.last_3_days.label"), sub: t("sweep.last_3_days.sub") },
  { id: "amount",      label: t("sweep.amount.label"),      sub: t("sweep.amount.sub") },
];

function SweepModal({ onRun, onClose }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("last_sweep");
  const [maxPosts, setMaxPosts] = useState(50);
  const SWEEP_MODES = getSweepModes(t);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 360, boxShadow: "0 8px 40px rgba(0,0,0,.18)" }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: "#1a1a1a", marginBottom: 4 }}>{t("sweep.title")}</div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 20 }}>{t("sweep.subtitle")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SWEEP_MODES.map(m => (
            <div key={m.id} onClick={() => setMode(m.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, cursor: "pointer", border: `2px solid ${mode === m.id ? BRAND.pink : "#e5e7eb"}`, background: mode === m.id ? "#fff5f8" : "#fafafa", transition: "all .15s" }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${mode === m.id ? BRAND.pink : "#ccc"}`, background: mode === m.id ? BRAND.pink : "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {mode === m.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: mode === m.id ? BRAND.pink : "#1a1a1a" }}>{m.label}</div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>{m.sub}</div>
              </div>
            </div>
          ))}
        </div>
        {mode === "amount" && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t("sweep.postsPerGroup")}</div>
            <Input type="number" value={maxPosts} onChange={e => setMaxPosts(Math.max(1, +e.target.value))} placeholder={t("sweep.postsPlaceholder")} style={{ width: 140 }} />
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <Btn onClick={() => onRun({ sinceMode: mode, maxPosts: mode === "amount" ? maxPosts : undefined })} style={{ flex: 1 }}>
            {t("sweep.run")}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>{t("sweep.cancel")}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const getNav = (t) => [
  { id: "dashboard", label: t("nav.dashboard"), icon: "dashboard" },
  { id: "leads", label: t("nav.leads"), icon: "🎯" },
  { id: "filters", label: t("nav.filters"), icon: "🔍", children: [
    { id: "filters/keywords", label: t("nav.filters/keywords") },
    { id: "filters/prompts", label: t("nav.filters/prompts") },
  ]},
  { id: "groups", label: t("nav.groups"), icon: "👥" },
  { id: "history", label: t("nav.history"), icon: "🕑" },
  { id: "settings", label: t("nav.settings"), icon: "⚙️" },
  { id: "users", label: t("nav.users"), icon: "👤", adminOnly: true },
];

function Sidebar({ active, setActive, sweepStatus, onSweep, currentUser, onLogout }) {
  const { t, dir, lang, setLang } = useI18n();
  const [expanded, setExpanded] = useState(["filters"]);
  const isActive = id => active === id || active.startsWith(id + "/");
  const toggleExpand = id => setExpanded(e => e.includes(id) ? e.filter(x => x !== id) : [...e, id]);
  const isRunning = sweepStatus?.isRunning;
  const NAV = getNav(t);
  const visibleNav = NAV.filter(item => !item.adminOnly || currentUser?.isAdmin);

  return (
    <div style={{ width: 220, background: "linear-gradient(180deg, #ff0076 0%, #ff0054 40%, #ff4d2a 100%)", display: "flex", flexDirection: "column", flexShrink: 0, height: "100vh", overflowY: "auto" }}>
      <div style={{ padding: "20px 18px 16px", borderBottom: "1px solid rgba(255,255,255,.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, background: "rgba(255,255,255,.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RadarIcon size={18} color={BRAND.lime} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>Pulseem</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,.6)", marginTop: -1, letterSpacing: 1, textTransform: "uppercase" }}>Lead Radar</div>
          </div>
        </div>
      </div>
      <div style={{ padding: "6px 10px", display: "flex", gap: 4, justifyContent: "center" }}>
        {["he", "en"].map(l => (
          <button key={l} onClick={() => setLang(l)} style={{ fontFamily: "Sen, sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${lang === l ? "#fff" : "rgba(255,255,255,.3)"}`, background: lang === l ? "rgba(255,255,255,.25)" : "transparent", color: lang === l ? "#fff" : "rgba(255,255,255,.55)" }}>
            {t(`lang.${l}`)}
          </button>
        ))}
      </div>
      <nav style={{ flex: 1, padding: "12px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
        {visibleNav.map(item => {
          const hasChildren = item.children?.length > 0;
          const open = expanded.includes(item.id);
          const itemActive = isActive(item.id);
          return (
            <div key={item.id}>
              <div onClick={() => { if (hasChildren) { toggleExpand(item.id); if (!open) setActive(item.children[0].id); } else setActive(item.id); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 9, cursor: "pointer", background: (!hasChildren && itemActive) ? "rgba(255,255,255,.25)" : "transparent", transition: "background .15s" }}>
                <span style={{ fontSize: 16, width: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {item.icon === "dashboard" ? <RadarIcon size={15} color="rgba(255,255,255,.85)" /> : item.icon}
                </span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,.9)" }}>{item.label}</span>
                {hasChildren && <span style={{ fontSize: 10, color: "rgba(255,255,255,.5)", transition: "transform .2s", display: "inline-block", transform: open ? "rotate(90deg)" : dir === "rtl" ? "rotate(180deg)" : "rotate(0deg)" }}>▶</span>}
                {!hasChildren && itemActive && <div style={{ width: 3, height: 16, borderRadius: 2, background: "#fff", flexShrink: 0 }} />}
              </div>
              {hasChildren && open && (
                <div style={{ marginLeft: dir === "rtl" ? 0 : 18, marginRight: dir === "rtl" ? 18 : 0, marginTop: 2, display: "flex", flexDirection: "column", gap: 1, borderLeft: dir === "rtl" ? "none" : "2px solid rgba(255,255,255,.2)", borderRight: dir === "rtl" ? "2px solid rgba(255,255,255,.2)" : "none", paddingLeft: dir === "rtl" ? 0 : 10, paddingRight: dir === "rtl" ? 10 : 0 }}>
                  {item.children.map(child => (
                    <div key={child.id} onClick={() => setActive(child.id)}
                      style={{ padding: "6px 10px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: active === child.id ? 700 : 500, color: active === child.id ? "#fff" : "rgba(255,255,255,.65)", background: active === child.id ? "rgba(255,255,255,.2)" : "transparent", transition: "all .15s", display: "flex", alignItems: "center", gap: 6 }}>
                      {active === child.id && <div style={{ width: 3, height: 12, borderRadius: 2, background: "#fff", flexShrink: 0 }} />}
                      {child.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div style={{ padding: "14px 12px", borderTop: "1px solid rgba(255,255,255,.2)", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "rgba(255,255,255,.9)", fontWeight: 600 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: isRunning ? "#facc15" : BRAND.lime, boxShadow: `0 0 0 3px ${isRunning ? "rgba(250,204,21,.3)" : "rgba(204,255,0,.3)"}` }} />
          {isRunning ? t("sidebar.sweeping") : sweepStatus?.schedulerActive ? t("sidebar.running") : t("sidebar.paused")}
        </div>
        <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,.15)", overflow: "hidden" }}>
          {isRunning && <div style={{ height: "100%", width: "35%", borderRadius: 2, background: "rgba(255,255,255,.9)", animation: "sweepBar 1.4s ease-in-out infinite" }} />}
        </div>
        <button onClick={onSweep} disabled={isRunning} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 12, cursor: isRunning ? "not-allowed" : "pointer", padding: "8px", borderRadius: 9, border: "2px solid rgba(255,255,255,.4)", background: "rgba(255,255,255,.15)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: isRunning ? .6 : 1 }}>
          {isRunning ? <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> {t("sidebar.sweeping")}</> : <><span>⚡</span> {t("sidebar.runSweep")}</>}
        </button>
        {currentUser && (
          <div style={{ marginTop: 8, borderTop: "1px solid rgba(255,255,255,.15)", paddingTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{currentUser.email}</div>
            <button onClick={onLogout} style={{ fontFamily: "Sen, sans-serif", fontSize: 10, fontWeight: 700, cursor: "pointer", padding: "3px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,.3)", background: "transparent", color: "rgba(255,255,255,.7)", flexShrink: 0, marginLeft: dir === "rtl" ? 0 : 6, marginRight: dir === "rtl" ? 6 : 0 }}>{t("sidebar.logout")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function DashboardTab() {
  const { t } = useI18n();
  const [timeRange, setTimeRange] = useState("24h");
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [apiConnected, setApiConnected] = useState(null);

  useEffect(() => {
    setApiConnected(null);
    apiFetch(`/api/dashboard/stats?timeRange=${timeRange}`)
      .then(data => { setStats(data); setApiConnected(true); })
      .catch(() => { setStats(DEFAULT_STATS); setApiConnected(false); });
  }, [timeRange]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t("dashboard.title")}</h1>
          <p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>{t("dashboard.subtitle")}</p>
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>

      {apiConnected === false && <ApiNotice />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          ["🔍", t("dashboard.postsScanned"), stats.postsScanned?.toLocaleString() ?? "0"],
          ["🎯", t("dashboard.qualifiedLeads"), stats.qualifiedLeads ?? "0"],
          ["📋", t("dashboard.pendingSend"), stats.pendingSend ?? "0"],
          ["📤", t("dashboard.sentToday"), stats.sentToday ?? "0"],
          ["💡", t("dashboard.aiCosts"), `$${(stats.aiCostUsd ?? 0).toFixed(3)}`],
          ["📈", t("dashboard.avgScore"), stats.avgScore ?? "0"],
        ].map(([icon, label, value]) => (
          <div key={label} style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 6px rgba(0,0,0,.06)", border: "1px solid #f0f0f0" }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{value}</div>
            <div style={{ fontSize: 11, color: BRAND.gray, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <Card>
          <SectionTitle>{t("dashboard.scoreBreakdown")}</SectionTitle>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            {[[t("dashboard.scoreHigh"), stats.scoreHigh, "#16a34a", "#dcfce7"], [t("dashboard.scoreMid"), stats.scoreMid, "#ca8a04", "#fef9c3"], [t("dashboard.scoreLow"), stats.scoreLow, BRAND.gray, "#f3f4f6"]].map(([l, c, col, bg]) => (
              <div key={l} style={{ flex: 1, background: bg, borderRadius: 10, padding: 12, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: col }}>{c ?? 0}</div>
                <div style={{ fontSize: 10, color: col, fontWeight: 700, marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>
          {stats.scoreChart?.length > 0 ? (
            <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 52 }}>
              {stats.scoreChart.sort((a, b) => new Date(a.date) - new Date(b.date)).map((item, i) => {
                const h = Math.max(4, (item.score / 100) * 48);
                const c = item.score >= 80 ? "#16a34a" : item.score >= 65 ? "#ca8a04" : "#d1d5db";
                return <div key={i} title={`Score: ${item.score}`} style={{ flex: 1, background: c, borderRadius: "3px 3px 0 0", height: h, minWidth: 5 }} />;
              })}
            </div>
          ) : (
            <div style={{ height: 52, background: "#f8f9fc", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: BRAND.gray }}>{t("dashboard.noScoreData")}</div>
          )}
        </Card>
        <Card>
          <SectionTitle>{t("dashboard.relevanceFeedback")}</SectionTitle>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            {[[t("dashboard.relevant"), stats.relevantTrue, "#16a34a", "#dcfce7"], [t("dashboard.notRelevant"), stats.relevantFalse, "#dc2626", "#fee2e2"], [t("dashboard.untagged"), stats.relevantUntagged, BRAND.gray, "#f3f4f6"]].map(([l, c, col, bg]) => (
              <div key={l} style={{ flex: 1, background: bg, borderRadius: 10, padding: 12, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: col }}>{c ?? 0}</div>
                <div style={{ fontSize: 10, color: col, fontWeight: 700, marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7 }}>
            {t("dashboard.positiveRate")} <strong style={{ color: "#16a34a" }}>{(stats.relevantTrue + stats.relevantFalse) > 0 ? Math.round(stats.relevantTrue / (stats.relevantTrue + stats.relevantFalse) * 100) : "—"}%</strong>
          </div>
          <div style={{ marginTop: 10, background: "#f8f9fc", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: BRAND.gray }}>
            {t("dashboard.needsReview", { n: stats.relevantUntagged ?? 0 })}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <Card>
          <SectionTitle>{t("dashboard.assignmentOverview")}</SectionTitle>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1, background: "#f0f4ff", borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#4f46e5" }}>{stats.unassignedLeads ?? 0}</div>
              <div style={{ fontSize: 10, color: "#4f46e5", fontWeight: 700, marginTop: 2, textTransform: "uppercase", letterSpacing: .5 }}>{t("dashboard.unassigned")}</div>
            </div>
            <div style={{ flex: 1, background: "#f0fdf4", borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#16a34a" }}>{(stats.leadsPerUser ?? []).reduce((s, u) => s + u.count, 0)}</div>
              <div style={{ fontSize: 10, color: "#16a34a", fontWeight: 700, marginTop: 2, textTransform: "uppercase", letterSpacing: .5 }}>{t("dashboard.assigned")}</div>
            </div>
          </div>
          {stats.leadsPerUser?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {stats.leadsPerUser.sort((a, b) => b.count - a.count).map(u => {
                const label = u.name || u.email?.split("@")[0] || u.email;
                const total = (stats.leadsPerUser ?? []).reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round((u.count / total) * 100) : 0;
                return (
                  <div key={u.email} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, minWidth: 120, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={u.email}>{label}</div>
                    <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 4, height: 8, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: BRAND.pink, borderRadius: 4, width: `${pct}%`, transition: "width .3s" }} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.pink, minWidth: 28, textAlign: "right" }}>{u.count}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: BRAND.gray, fontStyle: "italic" }}>{t("dashboard.noLeadsAssigned")}</div>
          )}
        </Card>

        <Card>
          <SectionTitle>{t("dashboard.quickStats")}</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              [t("dashboard.scoreHighFull"), stats.scoreHigh, "#16a34a", "#dcfce7"],
              [t("dashboard.scoreMidFull"), stats.scoreMid, "#ca8a04", "#fef9c3"],
              [t("dashboard.sentTodayFull"), stats.sentToday, "#4f46e5", "#f0f4ff"],
              [t("dashboard.pendingSendFull"), stats.pendingSend, BRAND.pink, "#fff0f5"],
            ].map(([l, v, col, bg]) => (
              <div key={l} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: bg, borderRadius: 8, padding: "8px 12px" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: col }}>{l}</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: col }}>{v ?? 0}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <SectionTitle>{t("dashboard.recentLeads")}</SectionTitle>
        {!stats.recentLeads?.length ? (
          <EmptyState message={t("dashboard.noLeads")} />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: "2px solid #f0f0f0" }}>{[t("col.score"), t("col.author"), t("col.group"), t("col.date"), t("col.status"), ""].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 14px", fontSize: 11, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
            <tbody>
              {stats.recentLeads.map(l => (
                <tr key={l.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td style={{ padding: "9px 14px" }}><Badge score={l.score} /></td>
                  <td style={{ padding: "9px 14px", fontWeight: 600 }}>{l.authorName}</td>
                  <td style={{ padding: "9px 14px", color: BRAND.gray, fontSize: 12 }}>{l.groupLabel}</td>
                  <td style={{ padding: "9px 14px", color: BRAND.gray, fontSize: 11 }}>{formatDate(l.date)}</td>
                  <td style={{ padding: "9px 14px" }}><span style={{ fontSize: 11, fontWeight: 700, color: l.sentAlert ? "#16a34a" : "#ca8a04", background: l.sentAlert ? "#dcfce7" : "#fef9c3", padding: "2px 8px", borderRadius: 20 }}>{l.sentAlert ? t("lead.sent") : t("lead.pending")}</span></td>
                  <td style={{ padding: "9px 14px" }}><FbBtn url={l.postUrl} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ─── Leads ────────────────────────────────────────────────────────────────────
function LeadsTab({ showToast }) {
  const { t } = useI18n();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiConnected, setApiConnected] = useState(true);
  const [timeRange, setTimeRange] = useState("all");
  const [minScore, setMinScore] = useState(0);
  const [relFilter, setRelFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState("grid");
  const [expanded, setExpanded] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    apiFetch("/api/users/list").then(setUsers).catch(() => {});
  }, []);

  const toggleCardText = (id, e) => {
    e.stopPropagation();
    setExpandedCards(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const loadLeads = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ timeRange, minScore, relevant: relFilter, sort });
    if (assigneeFilter !== "all") params.set("assignedTo", assigneeFilter);
    apiFetch(`/api/leads?${params}`)
      .then(data => { setLeads(data); setApiConnected(true); })
      .catch(() => { setLeads([]); setApiConnected(false); })
      .finally(() => setLoading(false));
  }, [timeRange, minScore, relFilter, sort, assigneeFilter]);

  const assignLead = async (leadId, userId) => {
    try {
      const res = await apiFetch(`/api/leads/${leadId}/assign`, { method: "PATCH", body: JSON.stringify({ userId: userId || null }) });
      setLeads(ls => ls.map(l => l.id === leadId ? { ...l, assignedTo: res.assignedTo, assignedEmail: res.assignedEmail } : l));
      showToast(userId ? t("leads.assignedTo", { email: res.assignedEmail }) : t("leads.unassignedDone"));
    } catch (e) { showToast(t("leads.failedAssign", { msg: e.message }), "error"); }
  };

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const setRelevant = async (id, val) => {
    try {
      await apiFetch(`/api/leads/${id}/relevant`, { method: "PATCH", body: JSON.stringify({ relevant: val }) });
      setLeads(ls => ls.map(l => l.id === id ? { ...l, relevant: val } : l));
    } catch (e) { showToast(t("leads.failedUpdate", { msg: e.message }), "error"); }
  };

  const exportCsv = () => {
    if (!leads.length) return showToast(t("leads.noExport"), "error");
    const headers = [t("csv.author"), t("csv.group"), t("csv.score"), t("csv.date"), t("csv.matchedKeywords"), t("csv.aiContext"), t("csv.pitchDraft"), t("csv.relevant"), t("csv.alertSent"), t("csv.postUrl")];
    const rows = leads.map(l => [
      l.authorName, l.groupLabel, l.score, l.date,
      (l.matchedKeywords || []).join("; "),
      l.aiContext || "", l.aiPitch || "",
      l.relevant === true ? t("csv.yes") : l.relevant === false ? t("csv.no") : t("csv.untagged"),
      l.sentAlert ? t("csv.yes") : t("csv.no"), l.postUrl || ""
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    showToast(t("leads.exported", { n: leads.length }));
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t("leads.title")}</h1><p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>{t("leads.subtitle", { n: leads.length })}</p></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Btn onClick={exportCsv} variant="ghost" small disabled={!leads.length}>{t("leads.exportCsv")}</Btn>
        <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 10, padding: 3 }}>
          {[["list", t("leads.viewList")], ["grid", t("leads.viewGrid")]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "6px 16px", borderRadius: 8, border: "none", background: view === v ? "#fff" : "transparent", color: view === v ? BRAND.pink : "#777", boxShadow: view === v ? "0 1px 6px rgba(0,0,0,.1)" : "none" }}>{l}</button>
          ))}
        </div>
        </div>
      </div>

      {!apiConnected && <ApiNotice />}

      <Card style={{ marginBottom: 16, padding: "14px 18px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .5 }}>{t("leads.filterTimeframe")}</div>
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          </div>
          <div style={{ width: 1, height: 44, background: "#e5e7eb", alignSelf: "center" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .5 }}>{t("leads.filterMinScore")}</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[[t("leads.all"), 0], ["65+", 65], ["80+", 80]].map(([l, v]) => <Pill key={l} label={l} active={minScore === v} onClick={() => setMinScore(v)} />)}
            </div>
          </div>
          <div style={{ width: 1, height: 44, background: "#e5e7eb", alignSelf: "center" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .5 }}>{t("leads.filterRelevance")}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <Pill label={t("leads.all")} active={relFilter === "all"} onClick={() => setRelFilter("all")} />
              <Pill label={t("leads.relevant")} active={relFilter === "true"} onClick={() => setRelFilter("true")} color="#16a34a" />
              <Pill label={t("leads.notRelevant")} active={relFilter === "false"} onClick={() => setRelFilter("false")} color="#dc2626" />
              <Pill label={t("leads.untagged")} active={relFilter === "null"} onClick={() => setRelFilter("null")} color={BRAND.gray} />
            </div>
          </div>
          <div style={{ width: 1, height: 44, background: "#e5e7eb", alignSelf: "center" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .5 }}>{t("leads.filterSort")}</div>
            <button onClick={() => setSort(s => s === "newest" ? "oldest" : "newest")} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${BRAND.pink}`, background: "#fff0f5", color: BRAND.pink, display: "flex", alignItems: "center", gap: 6 }}>
              {sort === "newest" ? "↓" : "↑"} {sort === "newest" ? t("leads.newestFirst") : t("leads.oldestFirst")}
            </button>
          </div>
          {users.length > 0 && <>
            <div style={{ width: 1, height: 44, background: "#e5e7eb", alignSelf: "center" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .5 }}>{t("leads.filterAssignedTo")}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Pill label={t("leads.all")} active={assigneeFilter === "all"} onClick={() => setAssigneeFilter("all")} />
                <Pill label={t("leads.unassigned")} active={assigneeFilter === "unassigned"} onClick={() => setAssigneeFilter("unassigned")} color={BRAND.gray} />
                {users.map(u => <Pill key={u.id} label={u.name || u.email.split("@")[0]} active={assigneeFilter === u.id} onClick={() => setAssigneeFilter(u.id)} />)}
              </div>
            </div>
          </>}
        </div>
      </Card>

      {loading ? <Spinner /> : leads.length === 0 ? <EmptyState message={t("leads.noLeads")} /> : null}

      {!loading && leads.length > 0 && view === "list" && (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ background: "#f8f9fc", borderBottom: "2px solid #f0f0f0" }}>
              {[t("col.score"), t("col.type"), t("col.author"), t("col.group"), t("col.preview"), t("col.date"), t("col.relevant"), ""].map(h => <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {leads.map(lead => (
                <React.Fragment key={lead.id}>
                  <tr onClick={() => setExpanded(expanded === lead.id ? null : lead.id)} style={{ cursor: "pointer", borderBottom: "1px solid #f5f5f5", background: expanded === lead.id ? "#fffbfc" : "transparent" }}>
                    <td style={{ padding: "10px 14px" }}><Badge score={lead.score} /></td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, background: lead.postType === "comment" ? "#f0fdf4" : "#f0f4ff", color: lead.postType === "comment" ? "#16a34a" : "#4f46e5", border: `1px solid ${lead.postType === "comment" ? "#bbf7d0" : "#c7d2fe"}`, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>
                        {lead.postType === "comment" ? t("lead.comment") : t("lead.post")}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", fontWeight: 600 }}>{lead.authorName}</td>
                    <td style={{ padding: "10px 14px", color: BRAND.gray, fontSize: 12 }}>{lead.groupLabel}</td>
                    <td style={{ padding: "10px 14px", maxWidth: 360, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} dir="auto">{lead.postPreview}</td>
                    <td style={{ padding: "10px 14px", color: BRAND.gray, fontSize: 11, whiteSpace: "nowrap" }}>{formatDate(lead.date)}</td>
                    <td style={{ padding: "10px 14px" }}><RelevanceBtns lead={lead} onSet={v => setRelevant(lead.id, v)} /></td>
                    <td style={{ padding: "10px 14px" }}><FbBtn url={lead.postUrl} /></td>
                  </tr>
                  {expanded === lead.id && (
                    <tr>
                      <td colSpan={8} style={{ padding: "18px 22px", background: "linear-gradient(135deg,#fff5f8,#fff)", borderBottom: "2px solid #ffd6e7" }}>
                        <div style={{ marginBottom: 10 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, background: lead.postType === "comment" ? "#f0fdf4" : "#f0f4ff", color: lead.postType === "comment" ? "#16a34a" : "#4f46e5", border: `1px solid ${lead.postType === "comment" ? "#bbf7d0" : "#c7d2fe"}`, padding: "2px 8px", borderRadius: 10 }}>
                            {lead.postType === "comment" ? t("lead.comment") : t("lead.post")}
                          </span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                          {[[lead.postType === "comment" ? t("lead.fullComment") : t("lead.fullPost"), lead.postTextFull], [t("lead.aiContext"), lead.aiContext], [t("lead.pitchDraft"), lead.aiPitch]].map(([lbl, c]) => (
                            <div key={lbl}><div style={{ fontSize: 11, fontWeight: 700, color: BRAND.pink, textTransform: "uppercase", marginBottom: 6 }}>{lbl}</div><div dir="auto" style={{ fontSize: 13, lineHeight: 1.6, color: "#374151", whiteSpace: "pre-wrap" }}>{c || "—"}</div></div>
                          ))}
                        </div>
                        {lead.matchedKeywords?.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", marginBottom: 5 }}>{t("lead.matchedKeywords")}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                              {lead.matchedKeywords.map(kw => <span key={kw} style={{ fontSize: 10, fontWeight: 700, background: "#fff0f5", color: BRAND.pink, border: `1px solid ${BRAND.pink}30`, padding: "2px 8px", borderRadius: 10 }}>{kw}</span>)}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {!loading && leads.length > 0 && view === "grid" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 16 }}>
          {leads.map(lead => {
            const isExp = expandedCards.has(lead.id);
            const isLong = (lead.postTextFull?.length ?? 0) > 200;
            return (
              <div key={lead.id} style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #f0f0f0", boxShadow: "0 2px 10px rgba(0,0,0,.06)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Header */}
                <div style={{ padding: "14px 16px 12px", borderBottom: "1px solid #f5f5f5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 15 }}>{lead.authorName}</div>
                      <div style={{ fontSize: 11, color: BRAND.gray, marginTop: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5 }}>
                        {lead.groupLabel} · {formatDate(lead.date)}
                        <span style={{ fontSize: 10, fontWeight: 700, background: lead.postType === "comment" ? "#f0fdf4" : "#f0f4ff", color: lead.postType === "comment" ? "#16a34a" : "#4f46e5", border: `1px solid ${lead.postType === "comment" ? "#bbf7d0" : "#c7d2fe"}`, padding: "1px 7px", borderRadius: 10, whiteSpace: "nowrap" }}>
                          {lead.postType === "comment" ? t("lead.comment") : t("lead.post")}
                        </span>
                      </div>
                    </div>
                    <Badge score={lead.score} large />
                  </div>
                </div>

                {/* Post text — clipped with View more */}
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #f5f5f5", flex: 1 }}>
                  <div style={{ position: "relative" }}>
                    <div dir="auto" style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap", maxHeight: isExp ? "none" : 90, overflow: "hidden" }}>
                      {lead.postTextFull}
                    </div>
                    {isLong && !isExp && (
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 36, background: "linear-gradient(to bottom, transparent, #fff)", pointerEvents: "none" }} />
                    )}
                  </div>
                  {isLong && (
                    <button onClick={e => toggleCardText(lead.id, e)} style={{ marginTop: 5, fontFamily: "Sen, sans-serif", fontSize: 11, fontWeight: 700, color: BRAND.pink, background: "none", border: "none", cursor: "pointer", padding: "2px 0", display: "block" }}>
                      {isExp ? t("lead.viewLess") : t("lead.viewMore")}
                    </button>
                  )}
                </div>

                {/* AI context + pitch */}
                <div style={{ background: "#f8f9fc", padding: "10px 16px", borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.pink, textTransform: "uppercase", marginBottom: 4 }}>{t("lead.context")}</div>
                  <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6, marginBottom: lead.aiPitch ? 10 : 0 }}>{lead.aiContext || "—"}</div>
                  {lead.aiPitch && <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", marginBottom: 4 }}>{t("lead.pitchDraft")}</div>
                    <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>{lead.aiPitch}</div>
                  </>}
                </div>

                {/* Keywords + prompt */}
                {(lead.matchedKeywords?.length > 0 || lead.matchedPrompt) && (
                  <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 7 }}>
                    {lead.matchedKeywords?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .4, marginBottom: 5 }}>{t("lead.matchedKeywords")}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {lead.matchedKeywords.map(kw => <span key={kw} style={{ fontSize: 10, fontWeight: 700, background: "#fff0f5", color: BRAND.pink, border: `1px solid ${BRAND.pink}30`, padding: "2px 8px", borderRadius: 10 }}>{kw}</span>)}
                        </div>
                      </div>
                    )}
                    {lead.matchedPrompt && (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase", letterSpacing: .4, marginBottom: 5 }}>{t("lead.aiPrompt")}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, background: "#f0f4ff", color: "#4f46e5", border: "1px solid #c7d2fe", padding: "2px 8px", borderRadius: 10 }}>{lead.matchedPrompt}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Assign */}
                {users.length > 0 && (
                  <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray, flexShrink: 0 }}>{t("leads.assign")}</span>
                    <select value={lead.assignedTo || ""} onChange={e => assignLead(lead.id, e.target.value || null)}
                      style={{ fontFamily: "Sen, sans-serif", fontSize: 12, border: "1.5px solid #e5e7eb", borderRadius: 7, padding: "4px 8px", background: "#fff", flex: 1, cursor: "pointer", color: lead.assignedTo ? "#1a1a1a" : BRAND.gray }}>
                      <option value="">{t("leads.unassignedOption")}</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email.split("@")[0]}</option>)}
                    </select>
                  </div>
                )}

                {/* Footer */}
                <div style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <RelevanceBtns lead={lead} onSet={v => setRelevant(lead.id, v)} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: lead.sentAlert ? "#16a34a" : "#ca8a04", background: lead.sentAlert ? "#dcfce7" : "#fef9c3", padding: "2px 8px", borderRadius: 20 }}>{lead.sentAlert ? t("lead.sent") : t("lead.pending")}</span>
                    <FbBtn url={lead.postUrl} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Keywords ─────────────────────────────────────────────────────────────────
function KeywordsTab({ type, showToast }) {
  const { t } = useI18n();
  const isExclude = type === "exclude";
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiConnected, setApiConnected] = useState(true);
  const [search, setSearch] = useState("");
  const [newWord, setNewWord] = useState("");
  const [newCat, setNewCat] = useState(isExclude ? "גיוס" : "כללי");
  const [recalibrating, setRecalibrating] = useState(false);
  const categories = isExclude ? ["גיוס", "פרילנס", "חינוך", "English", "כללי"] : ["כללי", "אימייל", "SMS", "WhatsApp", "AI"];

  const load = () => {
    setLoading(true);
    apiFetch(`/api/keywords?type=${type}`)
      .then(d => { setKeywords(d); setApiConnected(true); })
      .catch(() => { setKeywords([]); setApiConnected(false); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [type]);

  const addKw = async () => {
    if (!newWord.trim()) return;
    try {
      const created = await apiFetch("/api/keywords", { method: "POST", body: JSON.stringify({ name: newWord.trim(), category: newCat, type }) });
      setKeywords(kws => [...kws, created]);
      setNewWord("");
      showToast(isExclude ? t("keywords.excludeAdded") : t("keywords.added"));
    } catch (e) { showToast(e.message, "error"); }
  };

  const toggleKw = async (id, current) => {
    try {
      const updated = await apiFetch(`/api/keywords/${id}`, { method: "PATCH", body: JSON.stringify({ active: !current }) });
      setKeywords(kws => kws.map(k => k.id === id ? updated : k));
    } catch (e) { showToast(e.message, "error"); }
  };

  const deleteKw = async id => {
    try {
      await apiFetch(`/api/keywords/${id}`, { method: "DELETE" });
      setKeywords(kws => kws.filter(k => k.id !== id));
      showToast(t("keywords.deleted"));
    } catch (e) { showToast(e.message, "error"); }
  };

  const toggleAll = async val => {
    try {
      const updated = await Promise.all(keywords.map(k => apiFetch(`/api/keywords/${k.id}`, { method: "PATCH", body: JSON.stringify({ active: val }) })));
      setKeywords(updated);
    } catch (e) { showToast(e.message, "error"); }
  };

  const recalibrate = async () => {
    if (recalibrating) return;
    setRecalibrating(true);
    try {
      const res = await apiFetch("/api/keywords/recalibrate", { method: "POST" });
      showToast(t("keywords.recalibratedMsg", { n: res.keywordsWithData, s: res.keywordsWithData !== 1 ? "s" : "" }));
      load();
    } catch (e) { showToast(e.message, "error"); }
    finally { setRecalibrating(false); }
  };

  const filtered = keywords.filter(k => k.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      {!apiConnected && <ApiNotice />}
      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 20 }}>
        <Card style={{ padding: 16, alignSelf: "start" }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 14, textAlign: "center" }}>{t("keywords.searchGuide")}</div>
          {[{ icon: "🔍", title: t("keywords.guideExact"), ex: "מחפש מחשבת", result: t("keywords.guideExactResult") }, { icon: "+", title: t("keywords.guideBoth"), ex: "מחפש+מחשבת", result: t("keywords.guideBothResult") }, { icon: "❝", title: t("keywords.guideStem"), ex: '"מחפש"', result: t("keywords.guideStemResult") }].map(({ icon, title, ex, result }) => (
            <div key={title} style={{ background: "#f8f9fc", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: "#e8eaf0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>{icon}</div>
                <div><div style={{ fontWeight: 700, fontSize: 12 }}>{title}</div><code style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 4, padding: "1px 5px", fontSize: 11 }}>{ex}</code><div style={{ fontSize: 10, color: BRAND.gray, marginTop: 2 }}>→ {result}</div></div>
              </div>
            </div>
          ))}
        </Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card style={{ padding: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Input value={newWord} onChange={e => setNewWord(e.target.value)} placeholder={isExclude ? t("keywords.placeholderExclude") : t("keywords.placeholderInclude")} onKeyDown={e => e.key === "Enter" && addKw()} style={{ flex: 1 }} />
              <select value={newCat} onChange={e => setNewCat(e.target.value)} style={{ fontFamily: "Sen, sans-serif", fontSize: 12, border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "8px 10px", background: "#fff" }}>
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
              <Btn onClick={addKw} variant={isExclude ? "danger" : "primary"} small disabled={!newWord.trim()}>{t("add")}</Btn>
            </div>
          </Card>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8f9fc", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", flex: 1, maxWidth: 220 }}>
                <span style={{ color: BRAND.gray, fontSize: 12 }}>🔍</span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("keywords.searchPlaceholder")} style={{ border: "none", outline: "none", fontSize: 12, fontFamily: "Sen, sans-serif", background: "transparent", width: "100%" }} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => toggleAll(true)} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 11, cursor: "pointer", padding: "5px 10px", borderRadius: 7, border: "none", background: "#dcfce7", color: "#16a34a" }}>{t("keywords.allOn")}</button>
                <button onClick={() => toggleAll(false)} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 11, cursor: "pointer", padding: "5px 10px", borderRadius: 7, border: "none", background: "#fee2e2", color: "#dc2626" }}>{t("keywords.allOff")}</button>
                {!isExclude && (
                  <button onClick={recalibrate} disabled={recalibrating} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 11, cursor: recalibrating ? "default" : "pointer", padding: "5px 10px", borderRadius: 7, border: "none", background: "#eff6ff", color: "#2563eb", opacity: recalibrating ? 0.6 : 1 }}>
                    {recalibrating ? t("keywords.recalibrating") : t("keywords.recalibrate")}
                  </button>
                )}
              </div>
            </div>
            {loading ? <Spinner /> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ background: "#f8f9fc", borderBottom: "2px solid #f0f0f0" }}>{[t("col.keyword"), t("col.category"), isExclude ? t("col.blocked") : t("col.posts"), ...(!isExclude ? [t("col.signal")] : []), t("col.created"), t("col.active")].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 14px", fontSize: 11, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {filtered.map(kw => {
                    const sigTotal = (kw.matchedRelevant || 0) + (kw.matchedNotRelevant || 0);
                    const sigPct = sigTotal > 0 ? Math.round((kw.matchedRelevant / sigTotal) * 100) : null;
                    const sigColor = sigPct === null ? "#bbb" : sigPct >= 60 ? "#16a34a" : sigPct <= 40 ? "#dc2626" : "#ca8a04";
                    const sigLabel = sigPct === null || sigTotal < 5 ? "—" : `${sigPct >= 50 ? "↑" : "↓"}${sigPct}%`;
                    return (
                    <tr key={kw.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ padding: "9px 14px", fontWeight: 700 }}>{kw.name}</td>
                      <td style={{ padding: "9px 14px" }}><span style={{ background: isExclude ? "#fff1f2" : "#f3f4f6", color: isExclude ? "#e11d48" : "inherit", fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 600 }}>{kw.category}</span></td>
                      <td style={{ padding: "9px 14px", color: kw.postsMatched > 0 ? (isExclude ? "#e11d48" : "#16a34a") : BRAND.gray, fontWeight: 700 }}>{kw.postsMatched ?? 0}</td>
                      {!isExclude && <td style={{ padding: "9px 14px" }}><span style={{ color: sigColor, fontWeight: 700, fontSize: 12 }}>{sigLabel}</span>{sigTotal >= 5 && <span style={{ color: "#bbb", fontSize: 10, marginLeft: 4 }}>({sigTotal})</span>}</td>}
                      <td style={{ padding: "9px 14px", color: BRAND.gray, fontSize: 11 }}>{formatDate(kw.createdAt)}</td>
                      <td style={{ padding: "9px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Toggle active={kw.active} onToggle={() => toggleKw(kw.id, kw.active)} /><button onClick={() => deleteKw(kw.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 15, opacity: .6, padding: 0 }}>🗑</button></div></td>
                    </tr>
                  );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={isExclude ? 5 : 6}><EmptyState message={isExclude ? t("keywords.emptyExclude") : t("keywords.emptyInclude")} /></td></tr>}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function KeywordsPage({ showToast }) {
  const { t } = useI18n();
  const [tab, setTab] = useState("include");
  const tabBtn = active => ({
    fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer",
    padding: "7px 18px", borderRadius: 8, border: "2px solid",
    borderColor: active ? BRAND.pink : "#e5e7eb",
    background: active ? BRAND.pink : "#fff",
    color: active ? "#fff" : BRAND.gray,
    transition: "all .15s",
  });
  return (
    <div>
      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t("keywords.title")}</h1>
          <p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>
            {tab === "include" ? t("keywords.subtitleInclude") : t("keywords.subtitleExclude")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <button style={tabBtn(tab === "include")} onClick={() => setTab("include")}>{t("keywords.tabInclude")}</button>
          <button style={tabBtn(tab === "exclude")} onClick={() => setTab("exclude")}>{t("keywords.tabExclude")}</button>
        </div>
      </div>
      <KeywordsTab key={tab} type={tab} showToast={showToast} />
    </div>
  );
}

// ─── AI Prompts ───────────────────────────────────────────────────────────────
const getAnthropicModels = (t) => [
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6", badge: t("prompts.recommended") },
  { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5", badge: t("prompts.fastest") },
];

function PromptsPage({ showToast }) {
  const { t } = useI18n();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiConnected, setApiConnected] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newText, setNewText] = useState("");
  const [anthropicConfig, setAnthropicConfig] = useState({ keyConfigured: false, maskedKey: null, model: "claude-sonnet-4-6" });
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showCurrentKey, setShowCurrentKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [selectedModel, setSelectedModel] = useState("claude-sonnet-4-6");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiFetch("/api/prompts").catch(() => []),
      apiFetch("/api/config/anthropic").catch(() => ({ keyConfigured: false, maskedKey: null, model: "claude-sonnet-4-6" })),
    ]).then(([ps, cfg]) => {
      setPrompts(ps);
      setAnthropicConfig(cfg);
      setSelectedModel(cfg.model || "claude-sonnet-4-6");
      setApiConnected(true);
    }).catch(() => setApiConnected(false))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const togglePrompt = async id => {
    const p = prompts.find(x => x.id === id);
    try {
      await apiFetch(`/api/prompts/${id}`, { method: "PATCH", body: JSON.stringify({ active: !p.active }) });
      setPrompts(ps => ps.map(x => x.id === id ? { ...x, active: !x.active } : { ...x, active: false }));
    } catch (e) { showToast(e.message, "error"); }
  };

  const saveEdit = async id => {
    try {
      const updated = await apiFetch(`/api/prompts/${id}`, { method: "PATCH", body: JSON.stringify({ text: editText }) });
      setPrompts(ps => ps.map(p => p.id === id ? updated : p));
      setEditing(null);
      showToast(t("prompts.updated"));
    } catch (e) { showToast(e.message, "error"); }
  };

  const deleteP = async id => {
    try {
      await apiFetch(`/api/prompts/${id}`, { method: "DELETE" });
      setPrompts(ps => ps.filter(p => p.id !== id));
      showToast(t("prompts.deleted"));
    } catch (e) { showToast(e.message, "error"); }
  };

  const addP = async () => {
    if (!newName.trim() || !newText.trim()) return;
    try {
      const created = await apiFetch("/api/prompts", { method: "POST", body: JSON.stringify({ name: newName.trim(), text: newText.trim() }) });
      setPrompts(ps => [...ps, created]);
      setNewName(""); setNewText(""); setShowAdd(false);
      showToast(t("prompts.created"));
    } catch (e) { showToast(e.message, "error"); }
  };

  const saveAnthropicConfig = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      await apiFetch("/api/config/anthropic", { method: "PUT", body: JSON.stringify({ apiKey: apiKey.trim(), model: selectedModel }) });
      const cfg = await apiFetch("/api/config/anthropic");
      setAnthropicConfig(cfg);
      setApiKey("");
      setRevealedKey(null);
      setShowCurrentKey(false);
      showToast(t("prompts.saved"));
    } catch (e) { showToast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const activePrompt = prompts.find(p => p.active);

  return (
    <div>
      <div style={{ marginBottom: 24 }}><h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t("prompts.title")}</h1><p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>{t("prompts.subtitle")}</p></div>
      {!apiConnected && <ApiNotice />}

      <Card style={{ marginBottom: 20, border: "1.5px solid #e8eaf0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: BRAND.lime, fontSize: 16, fontWeight: 900 }}>✦</span>
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{t("prompts.anthropicTitle")}</div>
            <div style={{ fontSize: 12, color: BRAND.gray, marginTop: 1 }}>{t("prompts.anthropicSubtitle")}</div>
          </div>
          {anthropicConfig?.keyConfigured
            ? <span style={{ marginLeft: "auto", background: "#dcfce7", color: "#16a34a", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>{t("prompts.connected")}</span>
            : <span style={{ marginLeft: "auto", background: "#fee2e2", color: "#dc2626", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>{t("prompts.notConnected")}</span>
          }
        </div>
        {anthropicConfig?.maskedKey && (
          <div style={{ marginBottom: 12, fontSize: 12, color: BRAND.gray, background: "#f8f9fc", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ flexShrink: 0 }}>{t("prompts.currentKey")}</span>
            <code style={{ fontWeight: 700, color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {showCurrentKey && revealedKey ? revealedKey : anthropicConfig.maskedKey}
            </code>
            <button type="button" title={showCurrentKey ? t("prompts.hideKey") : t("prompts.showKey")}
              onClick={async () => {
                if (!showCurrentKey && !revealedKey) {
                  try { const r = await apiFetch("/api/config/anthropic/reveal"); setRevealedKey(r.apiKey); } catch { showToast(t("prompts.failedReveal"), "error"); return; }
                }
                setShowCurrentKey(v => !v);
              }}
              style={{ background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 2, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <EyeIcon open={showCurrentKey} />
            </button>
            <button type="button" title={t("prompts.copyKey")}
              onClick={async () => {
                let key = revealedKey;
                if (!key) { try { const r = await apiFetch("/api/config/anthropic/reveal"); setRevealedKey(r.apiKey); key = r.apiKey; } catch { showToast(t("prompts.failedCopy"), "error"); return; } }
                navigator.clipboard.writeText(key);
                showToast(t("copied"));
              }}
              style={{ background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 2, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, marginBottom: 16, alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{anthropicConfig?.keyConfigured ? t("prompts.updateApiKey") : t("prompts.apiKey")}</div>
            <div style={{ position: "relative" }}>
              <input type={showKey ? "text" : "password"} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk-ant-api03-..."
                dir="ltr" style={{ fontFamily: "Sen, sans-serif", fontSize: 13, border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 72px 9px 12px", outline: "none", width: "100%", boxSizing: "border-box" }} />
              <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", gap: 4 }}>
                {apiKey && (
                  <button type="button" onClick={() => { navigator.clipboard.writeText(apiKey); showToast(t("copied")); }}
                    title={t("prompts.copyKey")}
                    style={{ background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 2, display: "flex", alignItems: "center" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                )}
                <button type="button" onClick={() => setShowKey(s => !s)}
                  title={showKey ? t("prompts.hideKey") : t("prompts.showKey")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 2, display: "flex", alignItems: "center" }}>
                  <EyeIcon open={showKey} />
                </button>
              </div>
            </div>
          </div>
          <Btn onClick={saveAnthropicConfig} variant="primary" disabled={!apiKey.trim() || saving}>{saving ? t("saving") : t("prompts.saveKey")}</Btn>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10 }}>{t("prompts.model")}</div>
          <div style={{ display: "flex", gap: 10 }}>
            {getAnthropicModels(t).map(m => (
              <div key={m.id} onClick={() => setSelectedModel(m.id)}
                style={{ flex: 1, border: `2px solid ${selectedModel === m.id ? BRAND.pink : "#e5e7eb"}`, borderRadius: 10, padding: "11px 14px", cursor: "pointer", background: selectedModel === m.id ? "#fff5f8" : "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: selectedModel === m.id ? BRAND.pink : "#1a1a1a" }}>{m.label}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, background: selectedModel === m.id ? BRAND.pink : "#f3f4f6", color: selectedModel === m.id ? "#fff" : "#666", padding: "2px 7px", borderRadius: 10 }}>{m.badge}</span>
                </div>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${selectedModel === m.id ? BRAND.pink : "#d1d5db"}`, background: selectedModel === m.id ? BRAND.pink : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4 }}>
                  {selectedModel === m.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{t("prompts.scoringTitle")}</div>
          <div style={{ fontSize: 12, color: BRAND.gray, marginTop: 2 }}>
            {activePrompt ? <span>{t("prompts.activePrompt", { name: activePrompt.name })}</span> : t("prompts.noActivePrompt")}
          </div>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720 }}>
          {prompts.map(p => (
            <div key={p.id} style={{ background: p.active ? "linear-gradient(135deg,#fff5f8,#fff)" : "#fff", border: `2px solid ${p.active ? BRAND.pink : "#e5e7eb"}`, borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 14 }}>{p.name}</span>
                    {p.active && <span style={{ background: grad, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>{t("prompts.active")}</span>}
                    <span style={{ fontSize: 11, color: BRAND.gray, marginLeft: "auto" }}>{formatDate(p.createdAt)}</span>
                  </div>
                  {editing === p.id
                    ? <><Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={3} /><div style={{ display: "flex", gap: 8, marginTop: 8 }}><Btn onClick={() => saveEdit(p.id)} variant="primary" small>{t("save")}</Btn><Btn onClick={() => setEditing(null)} variant="ghost" small>{t("cancel")}</Btn></div></>
                    : <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>{p.text}</div>
                  }
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: p.active ? BRAND.pink : BRAND.gray, fontWeight: 600 }}>{p.active ? t("prompts.on") : t("prompts.off")}</span>
                    <Toggle active={p.active} onToggle={() => togglePrompt(p.id)} />
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn onClick={() => { setEditing(editing === p.id ? null : p.id); setEditText(p.text); }} variant="ghost" small>{t("edit")}</Btn>
                    <Btn onClick={() => deleteP(p.id)} variant="danger" small>🗑</Btn>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {showAdd
            ? <Card style={{ border: `2px dashed ${BRAND.pink}40` }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder={t("prompts.newPromptPlaceholder")} />
                <Textarea value={newText} onChange={e => setNewText(e.target.value)} placeholder={t("prompts.promptTextPlaceholder")} rows={4} />
                <div style={{ display: "flex", gap: 8 }}><Btn onClick={addP} variant="primary" small disabled={!newName.trim() || !newText.trim()}>{t("prompts.savePlus")}</Btn><Btn onClick={() => setShowAdd(false)} variant="ghost" small>{t("cancel")}</Btn></div>
              </div>
            </Card>
            : <button onClick={() => setShowAdd(true)} style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 12, borderRadius: 12, border: `2px dashed ${BRAND.pink}40`, background: "#fff9fb", color: BRAND.pink, width: "100%" }}>{t("prompts.addNew")}</button>
          }
        </div>
      )}
    </div>
  );
}

// ─── Groups ───────────────────────────────────────────────────────────────────
function GroupsTab({ showToast }) {
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiConnected, setApiConnected] = useState(true);
  const [newGroup, setNewGroup] = useState({ url: "", label: "" });
  const [fbSession, setFbSession] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const checkFbSession = () => {
    fetch("http://localhost:5001/status").then(r => r.json()).then(d => setFbSession(d.fbSessionSaved)).catch(() => setFbSession(false));
  };

  const connectFacebook = async () => {
    setLoginLoading(true);
    showToast(t("groups.loginOpening"), "info");
    try {
      const r = await fetch("http://localhost:5001/login", { method: "POST" });
      const d = await r.json();
      if (d.success) { showToast(t("groups.sessionSaved")); setFbSession(true); }
      else showToast(d.message || t("groups.loginTimeout"), "error");
    } catch (e) { showToast(t("groups.scraperError"), "error"); }
    setLoginLoading(false);
  };

  const load = () => {
    setLoading(true);
    apiFetch("/api/groups")
      .then(d => { setGroups(d); setApiConnected(true); })
      .catch(() => { setGroups([]); setApiConnected(false); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); checkFbSession(); }, []);

  const toggle = async (id, current) => {
    try {
      const updated = await apiFetch(`/api/groups/${id}`, { method: "PATCH", body: JSON.stringify({ active: !current }) });
      setGroups(g => g.map(x => x.id === id ? updated : x));
    } catch (e) { showToast(e.message, "error"); }
  };

  const del = async id => {
    try {
      await apiFetch(`/api/groups/${id}`, { method: "DELETE" });
      setGroups(g => g.filter(x => x.id !== id));
      showToast(t("groups.removed"));
    } catch (e) { showToast(e.message, "error"); }
  };

  const add = async () => {
    if (!newGroup.url.includes("facebook.com/groups/")) return showToast(t("groups.invalidUrl"), "error");
    try {
      const created = await apiFetch("/api/groups", { method: "POST", body: JSON.stringify(newGroup) });
      setGroups(g => [...g, created]);
      setNewGroup({ url: "", label: "" });
      showToast(t("groups.added"));
    } catch (e) { showToast(e.message, "error"); }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}><h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t("groups.title")}</h1><p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>{t("groups.subtitle")}</p></div>
      {!apiConnected && <ApiNotice />}
      {fbSession === false && (
        <div style={{ marginBottom: 16, padding: "14px 18px", background: "#fff7ed", border: "1.5px solid #f97316", borderRadius: 12, display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 22 }}>🔐</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#9a3412" }}>{t("groups.fbNotConnected")}</div>
            <div style={{ fontSize: 12, color: "#c2410c", marginTop: 2 }}>{t("groups.fbNotConnectedDesc")}</div>
          </div>
          <Btn onClick={connectFacebook} variant="primary" small disabled={loginLoading}>
            {loginLoading ? t("groups.waitingForLogin") : t("groups.connectFacebook")}
          </Btn>
        </div>
      )}
      {fbSession === true && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: "#f0fdf4", border: "1.5px solid #22c55e", borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <span>✅</span><span style={{ fontSize: 13, fontWeight: 700, color: "#15803d" }}>{t("groups.sessionActive")}</span>
        </div>
      )}
      <div style={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 14 }}>
        <Card>
          <SectionTitle sub={t("groups.activePaused", { active: groups.filter(g => g.active).length, paused: groups.filter(g => !g.active).length })}>{t("groups.activeGroups")}</SectionTitle>
          {loading ? <Spinner /> : groups.length === 0 ? <EmptyState message={t("groups.noGroups")} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {groups.map(g => (
                <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: g.active ? "#fff" : "#f8f9fc", borderRadius: 10, border: `1.5px solid ${g.active ? "#f0f0f0" : "#e5e7eb"}` }}>
                  <Toggle active={g.active} onToggle={() => toggle(g.id, g.active)} />
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 13, color: g.active ? "#1a1a1a" : BRAND.gray }}>{g.label}</div><div style={{ fontSize: 11, color: BRAND.gray, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>{g.url}</div></div>
                  <a href={g.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#1877f2", fontWeight: 700, textDecoration: "none", padding: "4px 10px", background: "#e7f0fd", borderRadius: 7, whiteSpace: "nowrap" }}>{t("groups.openLink")}</a>
                  <Btn onClick={() => del(g.id)} variant="danger" small>✕</Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card style={{ border: `2px dashed ${BRAND.pink}30` }}>
          <SectionTitle sub={t("groups.addNewSub")}>{t("groups.addNew")}</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr auto", gap: 10 }}>
            <Input value={newGroup.label} onChange={e => setNewGroup(p => ({ ...p, label: e.target.value }))} placeholder={t("groups.labelPlaceholder")} />
            <Input value={newGroup.url} onChange={e => setNewGroup(p => ({ ...p, url: e.target.value }))} placeholder={t("groups.urlPlaceholder")} onKeyDown={e => e.key === "Enter" && add()} dir="ltr" />
            <Btn onClick={add} variant="primary" disabled={!newGroup.url || !newGroup.label}>{t("groups.addButton")}</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Settings ─────────────────────────────────────────────────────────────────
function RecipientBlock({ emails, setEmails, newEmail, setNewEmail, showToast }) {
  const { t } = useI18n();
  const addEmail = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) return showToast(t("settings.invalidEmail"), "error");
    if (emails.includes(newEmail)) return showToast(t("settings.emailAlreadyAdded"), "error");
    setEmails([...emails, newEmail]); setNewEmail(""); showToast(t("settings.emailAdded"));
  };
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10 }}>{t("settings.emailAddresses")}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10, minHeight: 36 }}>
        {emails.map(e => (
          <div key={e} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8f9fc", borderRadius: 8, padding: "7px 12px", border: "1px solid #f0f0f0" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{e}</span>
            <button onClick={() => setEmails(emails.filter(x => x !== e))} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 16, opacity: .6, padding: 0 }}>×</button>
          </div>
        ))}
        {emails.length === 0 && <div style={{ fontSize: 12, color: BRAND.gray, fontStyle: "italic" }}>{t("settings.noEmails")}</div>}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder={t("settings.emailPlaceholder")} onKeyDown={e => e.key === "Enter" && addEmail()} style={{ flex: 1 }} dir="ltr" />
        <Btn onClick={addEmail} variant="primary" small disabled={!newEmail.trim()}>{t("add")}</Btn>
      </div>
    </div>
  );
}

function SettingsPage({ showToast, currentUser }) {
  const { t } = useI18n();
  const SWEEP_MODES = getSweepModes(t);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [apiConnected, setApiConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);

  const changePassword = async () => {
    if (!pwCurrent || !pwNew) return showToast(t("settings.fillBothFields"), "error");
    if (pwNew !== pwNew2) return showToast(t("settings.passwordMismatch"), "error");
    setPwSaving(true);
    try {
      await apiFetch("/api/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword: pwCurrent, newPassword: pwNew }) });
      showToast(t("settings.passwordChanged"));
      setPwCurrent(""); setPwNew(""); setPwNew2("");
    } catch (e) { showToast(e.message || t("settings.passwordChangeFailed"), "error"); }
    finally { setPwSaving(false); }
  };

  useEffect(() => {
    apiFetch("/api/settings")
      .then(data => { setSettings(data); setApiConnected(true); })
      .catch(() => { setSettings(DEFAULT_SETTINGS); setApiConnected(false); });
  }, []);

  const saveSettings = async (patch = {}) => {
    const merged = { ...settings, ...patch };
    setSaving(true);
    try {
      await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify(merged) });
      setSettings(merged);
      showToast(t("settings.saved"));
      setApiConnected(true);
    } catch (e) {
      setSettings(merged);
      showToast(t("settings.savedLocally"), "error");
    }
    finally { setSaving(false); }
  };

  const handleToggle = field => {
    const updated = { ...settings, [field]: !settings[field] };
    setSettings(updated);
    apiFetch("/api/settings", { method: "PUT", body: JSON.stringify(updated) })
      .then(() => setApiConnected(true))
      .catch(() => { });
  };

  const s = settings;
  const limits = { minutes: { min: 5, max: 59 }, hours: { min: 1, max: 23 }, days: { min: 1, max: 30 } };
  const { min, max } = limits[s.sweepUnit] || limits.minutes;
  const total = s.sweepUnit === "minutes" ? s.sweepValue : s.sweepUnit === "hours" ? s.sweepValue * 60 : s.sweepValue * 1440;
  const summary = total < 60 ? `${t('settings.every')} ${total} ${t('settings.minutes')}` : total < 1440 ? `${t('settings.every')} ${total / 60} ${t('settings.hours')}` : `${t('settings.every')} ${total / 1440} ${t('settings.days')}`;
  const Divider = () => <div style={{ height: 1, background: "#f0f0f0", margin: "32px 0" }} />;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t("settings.title")}</h1>
        <p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>{t("settings.subtitle")}</p>
      </div>

      {!apiConnected && <ApiNotice />}

      <div style={{ maxWidth: 740 }}>
        <SectionTitle sub={t('settings.sweepSchedulingSub')}>{t('settings.sweepScheduling')}</SectionTitle>
        <Card>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 10, background: s.schedulerEnabled ? "#fff5f8" : "#f8f9fc", border: `1.5px solid ${s.schedulerEnabled ? BRAND.pink + "30" : "#e5e7eb"}`, marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: s.schedulerEnabled ? BRAND.pink : "#1a1a1a" }}>
                {s.schedulerEnabled ? t('settings.schedulerEnabled') : t('settings.schedulerDisabled')}
              </div>
              <div style={{ fontSize: 12, color: BRAND.gray, marginTop: 3 }}>
                {s.schedulerEnabled ? t('settings.schedulerEnabledDesc') : t('settings.schedulerDisabledDesc')}
              </div>
            </div>
            <Toggle active={s.schedulerEnabled} onToggle={() => handleToggle("schedulerEnabled")} />
          </div>
          <div style={{ opacity: s.schedulerEnabled ? 1 : .4, pointerEvents: s.schedulerEnabled ? "auto" : "none" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#555", marginBottom: 12 }}>{t('settings.scanEvery')}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <input type="number" min={min} max={max} value={s.sweepValue}
                onChange={e => setSettings(prev => ({ ...prev, sweepValue: Math.min(max, Math.max(min, +e.target.value)) }))}
                style={{ fontFamily: "Sen, sans-serif", fontSize: 22, fontWeight: 800, border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "8px 14px", width: 88, textAlign: "center", outline: "none", color: BRAND.pink }} />
              <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 10, padding: 3, gap: 2 }}>
                {["minutes", "hours", "days"].map(u => (
                  <button key={u} onClick={() => setSettings(prev => ({ ...prev, sweepUnit: u, sweepValue: u === "minutes" ? 20 : 1 }))}
                    style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "6px 14px", borderRadius: 8, border: "none", background: s.sweepUnit === u ? "#fff" : "transparent", color: s.sweepUnit === u ? BRAND.pink : "#777", boxShadow: s.sweepUnit === u ? "0 1px 6px rgba(0,0,0,.1)" : "none" }}>{t(`settings.${u}`)}</button>
                ))}
              </div>
              <div style={{ background: "linear-gradient(135deg,#fff0f5,#ffe4ee)", border: `1.5px solid ${BRAND.pink}30`, borderRadius: 20, padding: "6px 16px", fontSize: 13, fontWeight: 700, color: BRAND.pink }}>{summary}</div>
            </div>
          </div>
        </Card>

        <div style={{ height: 16 }} />
        <SectionTitle sub={t('settings.sweepScopeSub')}>{t('settings.sweepScope')}</SectionTitle>
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {SWEEP_MODES.map(m => (
              <div key={m.id} onClick={() => setSettings(prev => ({ ...prev, autoSweepSinceMode: m.id }))}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, cursor: "pointer", border: `2px solid ${s.autoSweepSinceMode === m.id ? BRAND.pink : "#e5e7eb"}`, background: s.autoSweepSinceMode === m.id ? "#fff5f8" : "#fafafa", transition: "all .15s" }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${s.autoSweepSinceMode === m.id ? BRAND.pink : "#ccc"}`, background: s.autoSweepSinceMode === m.id ? BRAND.pink : "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {s.autoSweepSinceMode === m.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: s.autoSweepSinceMode === m.id ? BRAND.pink : "#1a1a1a" }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>{m.sub}</div>
                </div>
              </div>
            ))}
          </div>
          {s.autoSweepSinceMode === "amount" && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('settings.postsPerGroup')}</div>
              <Input type="number" value={s.autoSweepMaxPosts ?? 50}
                onChange={e => setSettings(prev => ({ ...prev, autoSweepMaxPosts: Math.max(1, +e.target.value) }))}
                placeholder={t('sweep.postsPlaceholder')} style={{ width: 140 }} />
            </div>
          )}
        </Card>

        <Divider />

        <SectionTitle sub={t('settings.instantAlertsSub')}>{t('settings.instantAlerts')}</SectionTitle>
        <Card>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 8 }}>{t('settings.scoreThreshold')}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="number" min={1} max={100} value={s.alertScoreThreshold ?? 65}
                onChange={e => setSettings(prev => ({ ...prev, alertScoreThreshold: Math.min(100, Math.max(1, +e.target.value)) }))}
                style={{ fontFamily: "Sen, sans-serif", fontSize: 22, fontWeight: 800, border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "8px 14px", width: 88, textAlign: "center", outline: "none", color: BRAND.pink }} />
              <div style={{ fontSize: 13, color: "#555" }}>
                {t('settings.alertWhenScore', { threshold: s.alertScoreThreshold ?? 65 })}
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
            <RecipientBlock
              emails={s.alertEmails || []}
              setEmails={v => setSettings(prev => ({ ...prev, alertEmails: v }))}
              newEmail={alertEmail} setNewEmail={setAlertEmail}
              showToast={showToast}
            />
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Btn onClick={async () => {
              try {
                const r = await apiFetch("/api/notifications/test", { method: "POST", body: "{}" });
                showToast(t('settings.testEmailSent', { recipients: r.recipients.join(", ") }));
              } catch (e) { showToast(e.message, "error"); }
            }} variant="ghost" small>{t('settings.sendTestEmail')}</Btn>
            <Btn onClick={async () => {
              try {
                const r = await apiFetch("/api/notifications/send-unsent", { method: "POST", body: "{}" });
                showToast(t('settings.unsentAlertsSent', { n: r.sent, s: r.sent !== 1 ? "s" : "", threshold: r.threshold }));
              } catch (e) { showToast(e.message, "error"); }
            }} variant="ghost" small>{t('settings.sendUnsent')}</Btn>
            <span style={{ fontSize: 11, color: BRAND.gray }}>{t('settings.sendUnsentDesc')}</span>
          </div>
        </Card>

        <Divider />

        <SectionTitle sub={t('settings.dailyReportSub')}>{t('settings.dailyReport')}</SectionTitle>
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10 }}>{t('settings.sendTime')}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8f9fc", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "8px 14px" }}>
              <input type="number" min={0} max={23} value={String(s.reportHour ?? 12).padStart(2, "0")}
                onChange={e => setSettings(prev => ({ ...prev, reportHour: Math.min(23, Math.max(0, +e.target.value)) }))}
                style={{ fontFamily: "Sen, sans-serif", fontSize: 22, fontWeight: 800, border: "none", background: "transparent", width: 52, textAlign: "center", outline: "none" }} />
              <span style={{ fontSize: 22, fontWeight: 900, color: BRAND.gray, lineHeight: 1 }}>:</span>
              <input type="number" min={0} max={59} value={String(s.reportMinute ?? 0).padStart(2, "0")}
                onChange={e => setSettings(prev => ({ ...prev, reportMinute: Math.min(59, Math.max(0, +e.target.value)) }))}
                style={{ fontFamily: "Sen, sans-serif", fontSize: 22, fontWeight: 800, border: "none", background: "transparent", width: 52, textAlign: "center", outline: "none" }} />
            </div>
            <select value={s.reportTimezone || "Asia/Jerusalem"} onChange={e => setSettings(prev => ({ ...prev, reportTimezone: e.target.value }))}
              style={{ fontFamily: "Sen, sans-serif", fontSize: 13, fontWeight: 600, border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "9px 14px", outline: "none", background: "#fff" }}>
              {["Asia/Jerusalem", "UTC", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Dubai"].map(tz => <option key={tz}>{tz}</option>)}
            </select>
            <div style={{ fontSize: 12, color: BRAND.gray, background: "#f8f9fc", borderRadius: 8, padding: "8px 12px" }}>
              {t('settings.next')} <strong style={{ color: BRAND.pink }}>{String(s.reportHour ?? 12).padStart(2, "0")}:{String(s.reportMinute ?? 0).padStart(2, "0")} {s.reportTimezone}</strong>
            </div>
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 14 }}>{t('settings.reportRecipients')}</div>
          <RecipientBlock
            emails={s.reportEmails || []}
            setEmails={v => setSettings(prev => ({ ...prev, reportEmails: v }))}
            newEmail={reportEmail} setNewEmail={setReportEmail}
            showToast={showToast}
          />
        </Card>

        <Divider />

        <SectionTitle sub={t('settings.aiOutputLangSub')}>{t('settings.aiOutputLang')}</SectionTitle>
        <Card>
          <div style={{ display: "flex", gap: 8 }}>
            {["Hebrew", "English", "Polish"].map(lng => (
              <button key={lng} onClick={() => setSettings(prev => ({ ...prev, outputLanguage: lng }))}
                style={{ fontFamily: "Sen, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: "10px 24px", borderRadius: 10, border: `2px solid ${s.outputLanguage === lng ? BRAND.pink : "#e5e7eb"}`, background: s.outputLanguage === lng ? "#fff0f5" : "#f8f9fc", color: s.outputLanguage === lng ? BRAND.pink : "#555", transition: "all .15s" }}>
                {t(`settings.lang${lng}`)}
              </button>
            ))}
          </div>
        </Card>

        <div style={{ marginTop: 28 }}>
          <Btn onClick={() => saveSettings()} variant="primary" disabled={saving}>{saving ? t('saving') : t('settings.saveAll')}</Btn>
        </div>

        <Divider />

        <SectionTitle sub={t('settings.changePasswordSub')}>{t('settings.changePassword')}</SectionTitle>
        <Card style={{ maxWidth: 440 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ position: "relative" }}>
              <Input type={showPwCurrent ? "text" : "password"} value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} placeholder={t('settings.currentPassword')} style={{ width: "100%", paddingRight: 40 }} dir="ltr" />
              <button onClick={() => setShowPwCurrent(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 0, display: "flex" }}>
                <EyeIcon open={showPwCurrent} />
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <Input type={showPwNew ? "text" : "password"} value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder={t('settings.newPassword')} style={{ width: "100%", paddingRight: 40 }} dir="ltr" />
              <button onClick={() => setShowPwNew(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 0, display: "flex" }}>
                <EyeIcon open={showPwNew} />
              </button>
            </div>
            {pwNew && (pwNew.length < 10 || !/[A-Z]/.test(pwNew) || !/[0-9]/.test(pwNew)) && (
              <div style={{ fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 10px" }}>
                {t('settings.passwordHint')}
              </div>
            )}
            <Input type="password" value={pwNew2} onChange={e => setPwNew2(e.target.value)} placeholder={t('settings.confirmPassword')} onKeyDown={e => e.key === "Enter" && changePassword()} dir="ltr" />
            <Btn onClick={changePassword} variant="primary" disabled={pwSaving || !pwCurrent || !pwNew || !pwNew2} small>{pwSaving ? t('saving') : t('settings.updatePassword')}</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Sweep History ────────────────────────────────────────────────────────────
function SweepHistoryPage() {
  const { t, dir } = useI18n();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/sweep/logs")
      .then(d => { setLogs(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const duration = (start, end) => {
    if (!start || !end) return "—";
    const s = Math.round((new Date(end) - new Date(start)) / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const statusBadge = s => {
    const map = { completed: ["#16a34a", "#dcfce7", t('history.completed')], failed: ["#dc2626", "#fee2e2", t('history.failed')], partial: ["#ca8a04", "#fef9c3", t('history.partial')], running: ["#3b82f6", "#eff6ff", t('history.running')] };
    const [col, bg, label] = map[s] || ["#6b7280", "#f3f4f6", s];
    return <span style={{ fontSize: 11, fontWeight: 700, color: col, background: bg, padding: "2px 9px", borderRadius: 20 }}>{label}</span>;
  };

  const totalCost = logs.reduce((s, l) => s + (l.aiCostUsd || 0), 0);
  const totalLeads = logs.reduce((s, l) => s + (l.newLeadsCreated || 0), 0);
  const totalPosts = logs.reduce((s, l) => s + (l.postsSeen || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{t('history.title')}</h1>
        <p style={{ color: BRAND.gray, fontSize: 13, marginTop: 4 }}>{t('history.subtitle', { n: logs.length })}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
        {[[t('history.totalPostsSeen'), totalPosts.toLocaleString()], [t('history.totalLeads'), totalLeads], [t('history.totalCost'), `$${totalCost.toFixed(4)}`]].map(([label, val]) => (
          <div key={label} style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", boxShadow: "0 1px 6px rgba(0,0,0,.06)", border: "1px solid #f0f0f0" }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{val}</div>
            <div style={{ fontSize: 11, color: BRAND.gray, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
          </div>
        ))}
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {loading ? <Spinner /> : logs.length === 0 ? <EmptyState message={t('history.noSweeps')} /> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8f9fc", borderBottom: "2px solid #f0f0f0" }}>
                {[t('col.started'), t('col.duration'), t('col.triggered'), t('col.postsSeen'), t('col.matched'), t('col.scored'), t('col.newLeads'), t('col.aiCost'), t('col.status')].map(h => (
                  <th key={h} style={{ textAlign: dir === "rtl" ? "right" : "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: BRAND.gray, textTransform: dir === "rtl" ? "none" : "uppercase", letterSpacing: dir === "rtl" ? 0 : 0.5, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", fontWeight: 600 }}>{formatDate(l.startedAt)}</td>
                  <td style={{ padding: "10px 14px", color: BRAND.gray }}>{duration(l.startedAt, l.completedAt)}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, background: l.triggeredBy === "manual" ? "#f0f4ff" : "#f0fdf4", color: l.triggeredBy === "manual" ? "#4f46e5" : "#16a34a", padding: "2px 8px", borderRadius: 20 }}>
                      {l.triggeredBy === "manual" ? t('history.manual') : t('history.scheduler')}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", fontWeight: 700 }}>{l.postsSeen}</td>
                  <td style={{ padding: "10px 14px", color: l.postsKeywordMatched > 0 ? "#ca8a04" : BRAND.gray }}>{l.postsKeywordMatched}</td>
                  <td style={{ padding: "10px 14px", color: l.postsScored > 0 ? "#4f46e5" : BRAND.gray }}>{l.postsScored}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontWeight: 800, color: l.newLeadsCreated > 0 ? "#16a34a" : BRAND.gray }}>{l.newLeadsCreated}</span>
                  </td>
                  <td style={{ padding: "10px 14px", color: BRAND.gray, fontFamily: "monospace" }}>
                    {l.aiCostUsd > 0 ? `$${l.aiCostUsd.toFixed(4)}` : "—"}
                  </td>
                  <td style={{ padding: "10px 14px" }}>{statusBadge(l.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function LoginPage({ onLogin }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [otpSession, setOtpSession] = useState(null);
  const [otpMethod, setOtpMethod] = useState("sms");
  const [otpCode, setOtpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  // Refs to read actual DOM values — needed because browser autofill sets the DOM
  // value directly without triggering React's onChange, leaving state empty.
  const emailRef = useRef();
  const passwordRef = useRef();
  const recaptchaRef = useRef();
  const [captchaDone, setCaptchaDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const cardStyle = { width: 360, background: "#fff", borderRadius: 18, boxShadow: "0 4px 32px rgba(0,0,0,.1)", padding: "36px 32px" };
  const inputStyle = { width: "100%", fontFamily: "Sen, sans-serif", fontSize: 14, border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "10px 14px" };
  const btnStyle = (disabled) => ({ width: "100%", fontFamily: "Sen, sans-serif", fontWeight: 800, fontSize: 15, cursor: disabled ? "default" : "pointer", padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#ff0076,#ff4d2a)", color: "#fff", opacity: disabled ? 0.7 : 1 });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const getCredentials = () => ({
    emailVal: (emailRef.current?.value || email).trim().toLowerCase(),
    passwordVal: passwordRef.current?.value || password,
  });

  const doLogin = async (emailVal, passwordVal, token) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailVal, password: passwordVal, captchaToken: token }),
    });
    if (!res.ok) throw new Error(t('login.invalidCredentials'));
    return res.json();
  };

  const submitLogin = async e => {
    e.preventDefault();
    if (!captchaDone) { setError(t('login.captchaRequired')); return; }
    const { emailVal, passwordVal } = getCredentials();
    setLoading(true); setError(null);
    try {
      const data = await doLogin(emailVal, passwordVal, captchaToken);
      if (data.requiresOtp) { setEmail(emailVal); setPassword(passwordVal); setOtpSession(data.otpSession); setOtpMethod(data.otpMethod ?? "sms"); setResendCooldown(60); return; }
      setToken(data.token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message || t('login.serverError'));
      recaptchaRef.current?.reset();
      setCaptchaDone(false);
      setCaptchaToken(null);
    }
    finally { setLoading(false); }
  };

  const resendCode = async () => {
    setError(null); setOtpCode("");
    setLoading(true);
    try {
      const data = await doLogin(email, password, captchaToken);
      if (data.requiresOtp) { setOtpSession(data.otpSession); setOtpMethod(data.otpMethod ?? "sms"); setResendCooldown(60); }
    } catch (err) { setError(err.message || t('login.resendError')); }
    finally { setLoading(false); }
  };

  const submitOtp = async e => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otpSession, code: otpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) { setError(t('login.tooManyAttempts')); setOtpSession(null); setOtpCode(""); }
        else setError(data?.error || data?.message || t('login.serverError'));
        return;
      }
      setToken(data.token);
      onLogin(data.user);
    } catch { setError(t('login.serverError')); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fc", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Sen, sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sen:wght@400;700;800&display=swap'); * { box-sizing:border-box; } input { font-family: Sen, sans-serif !important; } input:focus { border-color:#ff0054!important; outline:none; } input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0px 1000px #fff inset !important; -webkit-text-fill-color: #111 !important; font-family: Sen, sans-serif !important; transition: background-color 5000s ease-in-out 0s; }`}</style>
      <div style={cardStyle}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontWeight: 800, fontSize: 22, color: "#ff0054" }}>{t('login.title')}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{otpSession ? (otpMethod === "sms" ? t('login.smsVerification') : t('login.emailVerification')) : t('login.signIn')}</div>
        </div>
        {error && <div style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>}

        {!otpSession ? (
          <form onSubmit={submitLogin} autoComplete="on">
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('login.email')}</div>
              <input ref={emailRef} name="email" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required autoFocus dir="ltr" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('login.password')}</div>
              <div style={{ position: "relative" }}>
                <input ref={passwordRef} name="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required
                  dir="ltr" style={{ ...inputStyle, paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#888", padding: 0, display: "flex", alignItems: "center" }}>
                  <EyeIcon open={showPw} />
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                hl="en"
                onChange={token => { setCaptchaDone(!!token); setCaptchaToken(token || null); }}
                onExpired={() => { setCaptchaDone(false); setCaptchaToken(null); }}
              />
            </div>
            <button type="submit" disabled={loading || !captchaDone} style={btnStyle(loading || !captchaDone)}>
              {loading ? t('login.signingIn') : t('login.signInBtn')}
            </button>
          </form>
        ) : (
          <form onSubmit={submitOtp}>
            <p style={{ fontSize: 13, color: "#555", marginBottom: 20, lineHeight: 1.5 }}>
              {otpMethod === "sms" ? t('login.otpSentPhone') : t('login.otpSentEmail', { email })}
            </p>
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('login.verificationCode')}</div>
              <input type="text" autoComplete="one-time-code" inputMode="numeric" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6} autoFocus placeholder="000000" required
                style={{ ...inputStyle, textAlign: "center", fontSize: 24, letterSpacing: 8, fontWeight: 700 }} />
            </div>
            <button type="submit" disabled={loading || otpCode.length !== 6} style={btnStyle(loading || otpCode.length !== 6)}>
              {loading ? t('login.verifying') : t('login.verifyBtn')}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
              <button type="button" onClick={() => { setOtpSession(null); setOtpCode(""); setError(null); }}
                style={{ fontFamily: "Sen, sans-serif", fontSize: 13, color: "#888", background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}>
                {t('login.backToLogin')}
              </button>
              <button type="button" onClick={resendCode} disabled={loading || resendCooldown > 0}
                style={{ fontFamily: "Sen, sans-serif", fontSize: 13, color: resendCooldown > 0 ? "#bbb" : "#ff0054", background: "none", border: "none", cursor: resendCooldown > 0 ? "default" : "pointer", padding: "4px 0", fontWeight: 700 }}>
                {resendCooldown > 0 ? t('login.resendIn', { n: resendCooldown }) : t('login.resend')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Verify ───────────────────────────────────────────────────────────────────
function VerifyPage() {
  const { t } = useI18n();
  const token = new URLSearchParams(window.location.search).get("token");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("form"); // form | loading | done | error
  const [error, setError] = useState(null);
  const inputStyle = { width: "100%", fontFamily: "Sen, sans-serif", fontSize: 14, border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "10px 14px" };

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) { setError(t('verify.enterName')); return; }
    if (password !== confirm) { setError(t('verify.passwordMismatch')); return; }
    setStatus("loading"); setError(null);
    try {
      const res = await fetch(`${API}/api/auth/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, name: name.trim() }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.message || t('verify.invalidLink')); setStatus("form"); return; }
      setStatus("done");
    } catch { setError(t('verify.serverError')); setStatus("form"); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fc", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Sen, sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sen:wght@400;700;800&display=swap'); * { box-sizing:border-box; } input:focus { border-color:#ff0054!important; outline:none; }`}</style>
      <div style={{ width: 360, background: "#fff", borderRadius: 18, boxShadow: "0 4px 32px rgba(0,0,0,.1)", padding: "36px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontWeight: 800, fontSize: 22, color: "#ff0054" }}>{t('verify.title')}</div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{t('verify.subtitle')}</div>
        </div>
        {status === "done" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('verify.accountReady')}</div>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>{t('verify.canNowSignIn')}</div>
            <button onClick={() => window.location.replace("/")} style={{ fontFamily: "Sen, sans-serif", fontWeight: 800, fontSize: 14, cursor: "pointer", padding: "10px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#ff0076,#ff4d2a)", color: "#fff" }}>{t('verify.goToLogin')}</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            {error && <div style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('verify.yourName')}</div>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required autoFocus
                placeholder={t('verify.namePlaceholder')} autoComplete="name" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('verify.password')}</div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                placeholder={t('verify.passwordPlaceholder')} autoComplete="new-password" dir="ltr" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6 }}>{t('verify.confirmPassword')}</div>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                autoComplete="new-password" dir="ltr" style={inputStyle} />
            </div>
            <button type="submit" disabled={status === "loading"} style={{ width: "100%", fontFamily: "Sen, sans-serif", fontWeight: 800, fontSize: 15, cursor: "pointer", padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#ff0076,#ff4d2a)", color: "#fff", opacity: status === "loading" ? 0.7 : 1 }}>
              {status === "loading" ? t('verify.settingUp') : t('verify.createAccount')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Users ────────────────────────────────────────────────────────────────────
function UsersPage({ showToast }) {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otpMethod, setOtpMethod] = useState("sms");
  const [newEmail, setNewEmail] = useState("");
  const [newAdmin, setNewAdmin] = useState(false);
  const [creating, setCreating] = useState(false);
  // edit user modal: { id, email, name, phone }
  const [editModal, setEditModal] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  // set-password modal: { id, email }
  const [pwModal, setPwModal] = useState(null);
  const [pwVal, setPwVal] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);

  const load = () => {
    setLoading(true);
    apiFetch("/api/users").then(setUsers).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    apiFetch("/api/settings").then(s => setOtpMethod(s?.otpMethod ?? "sms")).catch(() => {});
  }, []);

  const saveOtpMethod = async (val) => {
    setOtpMethod(val);
    try {
      const current = await apiFetch("/api/settings");
      await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ ...current, otpMethod: val }) });
      showToast(t('users.otpSetTo', { method: val === "sms" ? "SMS" : "Email" }));
    } catch (e) { showToast(e.message, "error"); }
  };

  const createUser = async () => {
    if (!newEmail.trim()) return;
    setCreating(true);
    try {
      await apiFetch("/api/users", { method: "POST", body: JSON.stringify({ email: newEmail.trim(), isAdmin: newAdmin }) });
      showToast(t('users.inviteSent', { email: newEmail }));
      setNewEmail(""); setNewAdmin(false);
      load();
    } catch (e) { showToast(e.message, "error"); }
    finally { setCreating(false); }
  };

  const toggleAdmin = async (u) => {
    try {
      const updated = await apiFetch(`/api/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ isAdmin: !u.isAdmin }) });
      setUsers(us => us.map(x => x.id === u.id ? updated : x));
    } catch (e) { showToast(e.message, "error"); }
  };

  const openEditModal = (u) => { setEditModal(u); setEditName(u.name || ""); setEditPhone(u.phone || ""); };
  const closeEditModal = () => { setEditModal(null); };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const updated = await apiFetch(`/api/users/${editModal.id}`, { method: "PATCH", body: JSON.stringify({ name: editName, phone: editPhone }) });
      setUsers(us => us.map(x => x.id === editModal.id ? updated : x));
      closeEditModal();
      showToast(t('users.updated'));
    } catch (e) { showToast(e.message, "error"); }
    finally { setEditSaving(false); }
  };

  const openPwModal = (u) => { setPwModal(u); setPwVal(""); setShowPwModal(false); };
  const closePwModal = () => { setPwModal(null); setPwVal(""); };

  const setPassword = async () => {
    if (!pwVal) return;
    setPwSaving(true);
    try {
      await apiFetch(`/api/users/${pwModal.id}/password`, { method: "PATCH", body: JSON.stringify({ newPassword: pwVal }) });
      showToast(t('users.passwordUpdated', { email: pwModal.email }));
      closePwModal();
    } catch (e) { showToast(e.message, "error"); }
    finally { setPwSaving(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(t('users.confirmDelete', { email: u.email }))) return;
    try {
      await apiFetch(`/api/users/${u.id}`, { method: "DELETE" });
      setUsers(us => us.filter(x => x.id !== u.id));
      showToast(t('users.deleted'));
    } catch (e) { showToast(e.message, "error"); }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      {editModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: 380, boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{t('users.editUser')}</div>
            <div style={{ fontSize: 12, color: BRAND.gray, marginBottom: 18 }}>{editModal.email}</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray, marginBottom: 4, textTransform: "uppercase" }}>{t('users.name')}</div>
              <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder={t('users.fullName')} style={{ width: "100%" }}
                onKeyDown={e => e.key === "Enter" && saveEdit()} autoFocus />
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray, marginBottom: 4, textTransform: "uppercase" }}>{t('users.phone')}</div>
              <Input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder={t('users.phoneNumber')} style={{ width: "100%" }}
                onKeyDown={e => e.key === "Enter" && saveEdit()} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={saveEdit} disabled={editSaving} style={{ flex: 1 }}>{editSaving ? t('saving') : t('save')}</Btn>
              <Btn onClick={closeEditModal} variant="ghost">{t('cancel')}</Btn>
            </div>
          </div>
        </div>
      )}
      {pwModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: 380, boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{t('users.setPassword')}</div>
            <div style={{ fontSize: 12, color: BRAND.gray, marginBottom: 18 }}>{pwModal.email}</div>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <Input type={showPwModal ? "text" : "password"} value={pwVal} onChange={e => setPwVal(e.target.value)}
                placeholder={t('users.newPassword')} style={{ width: "100%", paddingRight: 40 }}
                onKeyDown={e => e.key === "Enter" && setPassword()} autoFocus dir="ltr" />
              <button onClick={() => setShowPwModal(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: BRAND.gray, padding: 0, display: "flex" }}>
                <EyeIcon open={showPwModal} />
              </button>
            </div>
            {pwVal && (pwVal.length < 10 || !/[A-Z]/.test(pwVal) || !/[0-9]/.test(pwVal)) && (
              <div style={{ fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 10px", marginBottom: 4 }}>
                {t('settings.passwordHint')}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={setPassword} disabled={pwSaving || !pwVal} style={{ flex: 1 }}>{pwSaving ? t('saving') : t('users.setPasswordBtn')}</Btn>
              <Btn onClick={closePwModal} variant="ghost">{t('cancel')}</Btn>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontWeight: 800, fontSize: 22, margin: 0 }}>{t('users.title')}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8f9fc", border: "1px solid #e5e7eb", borderRadius: 10, padding: "6px 14px" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray }}>{t('users.otpMethod')}</span>
          <button onClick={() => saveOtpMethod("email")} style={{ fontFamily: "Sen, sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "4px 12px", borderRadius: 8, border: "none", background: otpMethod === "email" ? BRAND.pink : "#fff", color: otpMethod === "email" ? "#fff" : "#555", boxShadow: otpMethod === "email" ? "0 1px 4px rgba(0,0,0,.15)" : "none" }}>
            {t('users.otpEmail')}
          </button>
          <button onClick={() => saveOtpMethod("sms")} style={{ fontFamily: "Sen, sans-serif", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "4px 12px", borderRadius: 8, border: "none", background: otpMethod === "sms" ? BRAND.pink : "#fff", color: otpMethod === "sms" ? "#fff" : "#555", boxShadow: otpMethod === "sms" ? "0 1px 4px rgba(0,0,0,.15)" : "none" }}>
            {t('users.otpSms')}
          </button>
        </div>
      </div>
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>{t('users.inviteNew')}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder={t('users.emailPlaceholder')} type="email" style={{ flex: 1 }} onKeyDown={e => e.key === "Enter" && createUser()} dir="ltr" />
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={newAdmin} onChange={e => setNewAdmin(e.target.checked)} /> {t('users.admin')}
          </label>
          <Btn onClick={createUser} disabled={creating || !newEmail.trim()} small>{creating ? "…" : t('users.sendInvite')}</Btn>
        </div>
      </Card>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 24, textAlign: "center" }}><Spinner /></div> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8f9fc", borderBottom: "2px solid #f0f0f0" }}>
                {[t('col.email'), t('col.name'), t('col.phone'), t('col.role'), t('col.status'), t('col.lastLogin'), ""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 14px", fontSize: 11, fontWeight: 700, color: BRAND.gray, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  <td style={{ padding: "10px 14px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</td>
                  <td style={{ padding: "10px 14px", color: u.name ? "#1a1a1a" : BRAND.gray, fontStyle: u.name ? "normal" : "italic" }}>{u.name || "—"}</td>
                  <td style={{ padding: "10px 14px", color: u.phone ? "#1a1a1a" : BRAND.gray, fontStyle: u.phone ? "normal" : "italic" }}>{u.phone || "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: u.isAdmin ? "#fff5f8" : "#f8f9fc", color: u.isAdmin ? BRAND.pink : "#666" }}>
                      {u.isAdmin ? t('users.roleAdmin') : t('users.roleUser')}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: u.isVerified ? "#16a34a" : "#ca8a04" }}>
                      {u.isVerified ? t('users.verified') : t('users.pending')}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", color: "#999", fontSize: 12, whiteSpace: "nowrap" }}>{u.lastLoginAt ? formatDate(u.lastLoginAt) : "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                      <button onClick={() => openEditModal(u)} style={{ fontFamily: "Sen, sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "3px 8px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#555" }}>
                        {t('users.editBtn')}
                      </button>
                      <button onClick={() => toggleAdmin(u)} style={{ fontFamily: "Sen, sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "3px 8px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#555", whiteSpace: "nowrap" }}>
                        {u.isAdmin ? t('users.removeAdmin') : t('users.makeAdmin')}
                      </button>
                      <button onClick={() => openPwModal(u)} style={{ fontFamily: "Sen, sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "3px 8px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#555" }} title={t('users.setPassword')}>
                        🔑
                      </button>
                      <button onClick={() => deleteUser(u)} style={{ fontFamily: "Sen, sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "3px 8px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626" }}>
                        {t('users.deleteBtn')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function AppInner() {
  const { t } = useI18n();
  // ── auth ──────────────────────────────────────────────────────────────────
  const isVerifyPage = window.location.pathname === "/verify";
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (isVerifyPage) {
      // If already logged in, kick them to the dashboard and erase the verify URL from history
      if (getToken()) { window.location.replace("/"); return; }
      setAuthChecked(true); return;
    }
    const token = getToken();
    if (!token) { setAuthChecked(true); return; }
    apiFetch("/api/auth/me")
      .then(user => { setCurrentUser(user); setAuthChecked(true); })
      .catch(() => { setToken(null); setAuthChecked(true); });
  }, []); // eslint-disable-line

  // Handle 401s from apiFetch (expired/invalid token) — show login without a page reload
  useEffect(() => {
    const handler = () => { setToken(null); setCurrentUser(null); };
    window.addEventListener("lr:unauthorized", handler);
    return () => window.removeEventListener("lr:unauthorized", handler);
  }, []);

  const handleLogout = () => { setToken(null); setCurrentUser(null); };

  // ── app state ─────────────────────────────────────────────────────────────
  const [active, setActive] = useState("dashboard");
  const [sweepStatus, setSweepStatus] = useState({ isRunning: false, schedulerActive: false });
  const [prevRunning, setPrevRunning] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const poll = () => {
      apiFetch("/api/sweep/status")
        .then(data => {
          if (!data) return;
          setSweepStatus(data);
          setPrevRunning(prev => {
            if (prev === true && data.isRunning === false) {
              showToast(t('app.sweepComplete', { n: data.lastSweep?.newLeadsCreated ?? 0 }));
            }
            return data.isRunning;
          });
        })
        .catch(() => { });
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [currentUser, showToast]);

  // SSE stream for live sweep events — fetches a short-lived ticket first to avoid JWT in URL/logs
  useEffect(() => {
    if (!currentUser) return;
    let es;
    apiFetch("/api/sweep/ticket")
      .then(({ ticket }) => {
        const url = `${API}/api/sweep/events?ticket=${encodeURIComponent(ticket)}`;
        es = new EventSource(url);
        es.addEventListener("sweep_started", () => setSweepStatus(s => ({ ...s, isRunning: true })));
        es.addEventListener("sweep_completed", () => setSweepStatus(s => ({ ...s, isRunning: false })));
        es.addEventListener("sweep_failed", () => setSweepStatus(s => ({ ...s, isRunning: false })));
        es.onerror = () => { /* reconnect handled by browser */ };
      })
      .catch(() => { /* SSE unavailable */ });
    return () => { if (es) es.close(); };
  }, [currentUser]); // reconnect when user changes

  const [showSweepModal, setShowSweepModal] = useState(false);

  const runSweep = async ({ sinceMode, maxPosts } = {}) => {
    setShowSweepModal(false);
    try {
      await apiFetch("/api/sweep/run", { method: "POST", body: JSON.stringify({ sinceMode: sinceMode ?? null, maxPosts: maxPosts ?? null }) });
      setSweepStatus(s => ({ ...s, isRunning: true }));
      setPrevRunning(true);
    } catch (e) {
      showToast(e.message.includes("409") ? t('app.sweepInProgress') : t('app.sweepFailed', { msg: e.message }), "error");
    }
  };

  // ── auth gates ───────────────────────────────────────────────────────────
  if (!authChecked) return null; // brief auth check — render nothing
  if (isVerifyPage) return <VerifyPage />;
  if (!currentUser) return <LoginPage onLogin={user => setCurrentUser(user)} />;

  return (
    <div style={{ fontFamily: "Sen, sans-serif", height: "100vh", overflow: "hidden", background: "#f8f9fc", color: "#1a1a1a", display: "flex" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sen:wght@400;700;800&display=swap');
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes slideUp { from { transform:translateY(20px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        @keyframes sweepBar { 0% { transform:translateX(-100%); } 100% { transform:translateX(350%); } }
        html, body { margin: 0; padding: 0; height: 100%; }
        :lang(he), :lang(he) * { font-family: Heebo, Sen, sans-serif; }
        * { box-sizing: border-box; }
        input:focus, textarea:focus, select:focus { border-color:${BRAND.pink}!important; box-shadow:0 0 0 3px rgba(255,0,84,.1); }
        button:hover:not(:disabled) { opacity:.88; }
        a:hover { opacity:.85; }
      `}</style>

      {showSweepModal && <SweepModal onRun={runSweep} onClose={() => setShowSweepModal(false)} />}
      <Sidebar active={active} setActive={setActive} sweepStatus={sweepStatus} onSweep={() => setShowSweepModal(true)} currentUser={currentUser} onLogout={handleLogout} />

      <main style={{ flex: 1, padding: "32px 36px", overflowY: "auto", height: "100vh" }}>
        {active === "dashboard" && <DashboardTab />}
        {active === "leads" && <LeadsTab showToast={showToast} />}
        {active === "filters/keywords" && <KeywordsPage showToast={showToast} />}
        {active === "filters/prompts" && <PromptsPage showToast={showToast} />}
        {active === "groups" && <GroupsTab showToast={showToast} />}
        {active === "history" && <SweepHistoryPage />}
        {active === "settings" && <SettingsPage showToast={showToast} currentUser={currentUser} />}
        {active === "users" && <UsersPage showToast={showToast} />}
      </main>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: toast.type === "success" ? "#1a1a1a" : "#dc2626", color: "#fff", padding: "12px 20px", borderRadius: 12, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,.2)", animation: "slideUp .3s ease" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  );
}
